import { useMemo } from 'react'
import { MapContainer, Marker, Popup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Globe2, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { OpenStreetMapTiles } from '@/components/maps/OpenStreetMapTiles'
import { useHrEmployeeMap } from '@/hooks/use-api'

// Brand-coloured teardrop pin (inline SVG divIcon). Navy fill distinguishes the
// employee map from the customer map's raspberry pin. White outline + drop shadow
// keep it visible over busy map tiles. No asset imports (avoids Leaflet's default
// marker bundler issue). Anchored at the tip.
const employeeIcon = L.divIcon({
  className: '',
  html:
    '<svg width="22" height="30" viewBox="0 0 22 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" ' +
    'style="filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.55))">' +
    '<path d="M11 1C5.5 1 1 5.4 1 10.8c0 6.7 8.4 17.1 9.5 18.4a1 1 0 0 0 1 .4 1 1 0 0 0 .5-.4C13 27.9 21 17.5 21 10.8 21 5.4 16.5 1 11 1z" ' +
    'fill="#183E6A" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="11" cy="10.8" r="3.2" fill="#fff"/>' +
    '</svg>',
  iconSize: [22, 30],
  iconAnchor: [11, 30],
  popupAnchor: [0, -28],
})

// Accessible, on-brand cluster icon. Colour ramps light -> dark with magnitude
// ("darker = more people"); text colour flips (dark on the light tier, white on
// the dark tiers) so the count always passes AA contrast. Size also scales.
const createClusterIcon = (cluster: { getChildCount: () => number }) => {
  const count = cluster.getChildCount()
  const { bg, fg, size } =
    count >= 100
      ? { bg: '#072744', fg: '#ffffff', size: 52 } // Prussian, white (~15:1)
      : count >= 25
        ? { bg: '#183E6A', fg: '#ffffff', size: 46 } // Navy, white (~10:1)
        : { bg: '#A0C1DB', fg: '#072744', size: 40 } // Powder Blue, Prussian text (~8:1)
  const shadow = fg === '#ffffff' ? 'text-shadow:0 1px 2px rgba(0,0,0,.5);' : ''
  return L.divIcon({
    className: '',
    html:
      `<div role="img" aria-label="${count} employees in this area" title="${count} employees in this area" ` +
      `style="width:${size}px;height:${size}px;border-radius:9999px;background:${bg};` +
      `border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35);` +
      `display:flex;align-items:center;justify-content:center;` +
      `color:${fg};font-weight:700;font-size:13px;${shadow}">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

export default function EmployeeWorldMap() {
  const { data, isLoading, error } = useHrEmployeeMap()
  const employees = useMemo(() => data?.employees ?? [], [data])

  const byCountry = useMemo(() => {
    const m = new Map<string, number>()
    for (const e of employees) {
      const key = (e.country || 'Unknown').trim() || 'Unknown'
      m.set(key, (m.get(key) || 0) + 1)
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [employees])

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Globe2 className="h-7 w-7 text-primary" aria-hidden="true" />
          Where in the World
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data
            ? `${data.located_count.toLocaleString()} active employees across ${data.country_count} ${
                data.country_count === 1 ? 'country' : 'countries'
              }`
            : 'Active employees around the globe'}
          {data && data.unlocated_count > 0
            ? ` · ${data.unlocated_count.toLocaleString()} without a mappable location`
            : ''}
        </p>
      </div>

      {isLoading && (
        <div
          className="flex flex-col items-center justify-center gap-2 py-20 text-center text-muted-foreground"
          role="status"
        >
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
          <div>
            <p>Locating employees…</p>
            <p className="text-sm">
              The first load can take a moment while we map each location. After that it loads
              quickly.
            </p>
          </div>
        </div>
      )}
      {error && (
        <p className="text-destructive" role="alert">
          Failed to load the map. Please try again.
        </p>
      )}

      {data && (
        <>
          <Card>
            <CardContent className="p-0">
              {/* `isolate` confines Leaflet's z-index panes to their own stacking
                  context so overlays render above the map instead of behind it. */}
              <div
                className="isolate h-[70vh] w-full overflow-hidden rounded-md"
                role="region"
                aria-label="World map of active employees"
              >
                <MapContainer
                  center={[20, 0]}
                  zoom={2}
                  minZoom={2}
                  worldCopyJump
                  scrollWheelZoom
                  style={{ height: '100%', width: '100%' }}
                >
                  <OpenStreetMapTiles />
                  <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon}>
                    {employees.map((e) => (
                      <Marker key={e.id} position={[e.lat, e.lng]} icon={employeeIcon}>
                        <Popup>
                          <div className="text-sm">
                            <div className="font-semibold">{e.name}</div>
                            {(e.job_title || e.department) && (
                              <div>{[e.job_title, e.department].filter(Boolean).join(' · ')}</div>
                            )}
                            <div className="text-muted-foreground">
                              {[e.city, e.state, e.country].filter(Boolean).join(', ')}
                            </div>
                            {e.work_location && <div className="mt-1">{e.work_location}</div>}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              </div>
            </CardContent>
          </Card>

          {/* Accessible companion: same data as the map, for keyboard/screen-reader users. */}
          <details className="rounded-md border bg-card">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
              Employees by country (accessible list)
            </summary>
            <div className="px-4 pb-4 overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">Active employee counts by country</caption>
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th scope="col" className="py-1">
                      Country
                    </th>
                    <th scope="col" className="py-1 text-right">
                      Employees
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {byCountry.map(([country, count]) => (
                    <tr key={country} className="border-t">
                      <td className="py-1">{country}</td>
                      <td className="py-1 text-right">{count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </div>
  )
}
